// Texts of the Project Management page and its migrated dialogs.
const projectManagement = {
  title: 'Project Management',
  subtitle: 'Search and filter projects; manage their team, visits, patients and files.',
  listLabel: 'Project list',
  yes: 'Yes',
  no: 'No',
  status: {
    active: 'Active',
    inactive: 'Inactive',
    all: 'All',
  },
  stats: {
    total: 'Total projects',
    totalShort: 'Total',
    active: 'Active projects',
    activeShort: 'Active',
    inactive: 'Inactive projects',
    inactiveShort: 'Inactive',
    share: 'Share of all projects: {{percent}}%',
  },
  actions: {
    add: 'Add Project',
    addTest: 'Add Test Project',
    stats: 'Statistics',
    download: 'Download Table',
    downloadHint: 'Download every project as Excel, with the columns shown in the table',
  },
  toggles: {
    dueSoon: 'Ending soon',
    dueSoonHint: 'Projects whose end date is within the next month',
    testOnly: 'Test projects',
    testOnlyHint: 'Show test projects only',
  },
  search: {
    placeholder: 'Search customer, project or department…',
    placeholderShort: 'Search projects…',
    label: 'Search projects (at least 3 characters)',
  },
  columns: {
    customer: 'Customer',
    titleShort: 'Title (Short)',
    projectPhase: 'Project Phase',
    projectStatus: 'Project Status',
    protocolNumber: 'Protocol Number',
    ethicsCommitteeDate: 'Ethics Committee Date',
    ethicsCommitteeNumber: 'Ethics Committee No.',
    ethicsCommitteePlace: 'Ethics Committee',
    firstInvoiceDate: 'First Invoice Date',
    paymentFrequency: 'Payment Frequency',
    projectCentersType: 'Centre Type',
    projectDesign: 'Project Design',
    projectDate: 'Project Date',
    projectDepartment: 'Department',
    projectDueDate: 'End Date',
    coordinatorAcademician: 'Coordinating Academic',
    sbFirstApprovalDate: 'MoH First Approval Date',
    sbFirstApprovalNumber: 'MoH First Approval No.',
    sbProjectNumber: 'MoH Project No.',
    titleLong: 'Title (Long)',
    isCurrencyFixed: 'Fixed Exchange Rate',
    fixedCurrencyRate: 'Fixed Rate',
  },
  phases: {
    initiation: 'Initiation',
    planning: 'Planning',
    execution: 'Execution',
    monitoring: 'Monitoring & Control',
    closing: 'Closing',
    completed: 'Completed',
    phase1: 'Phase I',
    phase2: 'Phase II',
    phase3: 'Phase III',
    lowRisk: 'Low-risk scientific study',
    adaptive12: 'Adaptive (Phase I/II)',
    adaptive13: 'Adaptive (Phase I/III)',
    adaptive23: 'Adaptive (Phase II/III)',
    healthClaim: 'Health-claim product/method',
    observational: 'Observational study',
    stemCell: 'Stem-cell research',
  },
  designs: {
    doubleBlind: 'Double-blind',
    openLabel: 'Open label',
    hybrid: 'Hybrid',
  },
  badges: {
    finished: 'Finished',
    test: 'Test',
  },
  due: {
    daysLeft_one: '{{count}} day left',
    daysLeft_other: '{{count}} days left',
    today: 'Ends today',
    overdue_one: '{{count}} day overdue',
    overdue_other: '{{count}} days overdue',
  },
  table: {
    empty: 'No projects match the filters',
  },
  pagination: {
    rangeOf: 'of {{total}} projects',
    ofTotal: 'out of {{total}} in total',
  },
  panel: {
    label: 'Project details',
    heading: 'Project',
    card: 'Project Card',
    edit: 'Edit',
    workers: 'Staff',
    visits: 'Visits',
    patients: 'Patients',
    files: 'Files',
    screenFail: 'Screen Fail',
    finish: 'Finish Project',
    unfinish: 'Undo Finish',
    finishHint: 'Marks the project as finished; it stays active',
    deactivate: 'Set Inactive',
    activate: 'Set Active',
    statusHint: 'Change the project status (Active/Inactive)',
  },
  confirm: {
    title: 'Confirmation needed',
    cancel: 'Cancel',
    confirm: "Yes, I'm sure",
    status: "Change the project status to '{{status}}'?",
    finish: 'Mark "{{name}}" as finished? The project will stay active.',
    unfinish: 'Remove the "finished" mark from "{{name}}"?',
    projectFallback: 'Project',
  },
  toast: {
    loadError: 'Projects could not be loaded!',
    excelPreparing: 'Preparing the Excel file...',
    excelSuccess: 'Excel file downloaded',
    excelError: 'The file could not be downloaded',
    statusUpdated: 'Project status updated to "{{status}}"',
    statusError: 'The status could not be updated',
    finished: 'Project marked as finished',
    unfinished: 'The "finished" mark was removed',
    finishError: 'Something went wrong',
    testCreating: 'Creating the test project...',
    testCreated: 'Test project and tariffs added! Tasks created for {{count}} people.',
    testError: 'The test project could not be added: {{message}}',
    screenFailError: 'Screen fail records could not be loaded',
  },
  screenFail: {
    title: 'Screen Fail Records',
    loading: 'Loading...',
    empty: 'This project has no screen fail records.',
    total: 'Total screen fails',
    employees: 'Staff',
    patients: 'Patients',
    date: 'Date',
    employee: 'Staff member',
    patientId: 'Patient ID',
    visitNumber: 'Visit No.',
    info: 'Info',
  },
  statsModal: {
    title: 'Project Statistics',
    subtitle: '{{count}} projects',
    total: 'Total projects',
    active: 'Active projects',
    inactive: 'Inactive projects',
    statusTitle: 'Project status',
    phaseTitle: 'Project phases',
    unspecified: 'Not specified',
  },
  form: {
    addTitle: 'Add Project',
    editTitle: 'Edit Project',
    addSubtitle: 'Enter the project details',
    submitAdd: 'Add Project',
    submitEdit: 'Save',
    sections: {
      general: 'General',
      generalHint: 'Customer, project names, protocol and coordinator',
      study: 'Study',
      studyHint: 'Department, phase, design, centre and dates',
      approvals: 'Approvals',
      approvalsHint: 'Ethics committee and Ministry of Health approvals',
      finance: 'Finance',
      financeHint: 'Invoicing, payment frequency and fixed exchange rate',
    },
    fields: {
      customer: 'Customer',
      titleShort: 'Project name (short)',
      titleLong: 'Project name (long)',
      protocolNumber: 'Protocol number',
      coordinatorAcademician: 'Coordinating academic',
      projectDepartment: 'Department',
      projectPhase: 'Project phase',
      projectDesign: 'Project design',
      projectCentersType: 'Centre type',
      center: 'Centre',
      projectDate: 'Project date',
      projectDueDate: 'End date',
      ethicsCommitteeDate: 'Ethics committee date',
      ethicsCommitteeNumber: 'Ethics committee number',
      ethicsCommitteePlace: 'Ethics committee',
      sbFirstApprovalDate: 'MoH first approval date',
      sbFirstApprovalNumber: 'MoH first approval number',
      sbProjectNumber: 'MoH project number',
      firstInvoiceDate: 'First invoice date',
      paymentFrequency: 'Payment frequency',
      isCurrencyFixed: 'Is the exchange rate fixed?',
      fixedCurrencyRate: 'Fixed exchange rate',
    },
    placeholders: {
      customer: 'Select a customer...',
      titleShort: 'Enter a short title...',
      titleLong: 'Enter a long title...',
      coordinator: 'Select the coordinating academic...',
      department: 'Select a department...',
      phase: 'Select a phase...',
      design: 'Double-blind, open label or hybrid...',
      centerType: 'Select a centre type...',
      center: 'Select a centre...',
      rate: 'e.g. 32.1500',
    },
    hints: {
      hybrid: "In a hybrid project, a visit opened by the primary and blinded staff also appears in the calendar of the project's unblinded staff.",
      hybridEdit: 'The change only affects new visits.',
      paymentFrequency: "Filled from the customer card's payment period when a customer is chosen",
    },
    centerTypes: {
      home: 'Home',
      hospital: 'Hospital',
      concurrent: 'Concurrent',
    },
    errors: {
      required: 'Please fill in the required fields!',
      rate: 'A fixed rate was chosen. Please enter a valid rate.',
    },
    toast: {
      customersError: 'Customers could not be loaded!',
      centersError: 'Centres could not be loaded!',
      personnelError: 'External staff could not be loaded!',
      customerInfoError: 'Customer details could not be read!',
      added: 'Project added! Tasks created for {{count}} people.',
      addError: 'The project could not be added!',
      updated: 'Project updated!',
      updateError: 'The project could not be updated!',
    },
  },
  files: {
    title: 'Files',
    type: 'File type',
    chooseType: 'Please choose a file type',
    typeFirst: 'Choose the file type first',
    drop: 'Drag your file here',
    or: 'or',
    browse: 'Choose File',
    uploading: 'Uploading...',
    uploaded: '"{{name}}" uploaded',
    uploadError: 'The file could not be uploaded',
    list_one: 'Uploaded files ({{count}})',
    list_other: 'Uploaded files ({{count}})',
    loading: 'Loading files...',
    none: 'No files uploaded yet',
    open: 'Open file',
    delete: 'Delete',
    keep: 'Keep',
    confirmDelete: 'Delete it?',
    deleted: 'File deleted',
    deleteError: 'The file could not be deleted: {{message}}',
    types: {
      ilkBakanlikOnayi: 'First Ministry approval',
      ilkEtikKurulOnayi: 'First ethics committee approval',
      etikKurulSunumu: 'Ethics committee presentation',
      etikKurulKarari: 'Ethics committee decision',
      etikKurulOnayi: 'Ethics committee approval',
      titck: 'TİTCK',
      diger: 'Other',
    },
  },
  visits: {
    title: 'Visits',
    add: 'Add Visit',
    count_one: '{{count}} visit',
    count_other: '{{count}} visits',
    loading: 'Loading visits...',
    empty: 'This project has no visits yet.',
    employee: 'Staff member',
    firstVisitDate: 'First visit date',
    period: 'Window',
    patientId: 'Patient ID',
    visitNumber: 'Visit No.',
    loadError: 'Visits could not be loaded!',
  },
  addVisit: {
    title: 'Add Visit',
    choose: 'Please select',
    employee: 'Staff member',
    patient: 'Patient ID',
    patientOptional: 'Select a patient (optional)',
    patientsLoading: 'Loading patients...',
    noPatients: 'No patients are registered in this project',
    visitDate: 'First visit date',
    visitType: 'Visit type',
    types: {
      hastane: 'Hospital visit',
      home: 'Home visit',
      telefon: 'Phone visit',
      unplanning: 'Unplanned visit',
      handover: 'Handover visit',
    },
    window: 'Visit window',
    windowStart: 'Start date',
    windowEnd: 'End date',
    windowVisit: 'Visit:',
    windowDetail: 'Window: starts {{before}} days before, ends {{after}} days after ({{total}} days in total)',
    visitNumber: 'Visit number',
    visitNumberPlaceholder: 'e.g. 1, 2, 3...',
    suggestedSame: "This patient's next visit in this project: {{n}}. You can change it.",
    suggested: 'Suggested: {{n}}',
    screenFail: 'Screen fail?',
    screenFailNote: 'Records marked as screen fail do not count as visits; they are recorded as work done.',
    info: 'Info',
    repeat: 'Will it repeat?',
    calendar: 'Add this visit to my Google Calendar',
    endDate: 'Closing visit date',
    repeatPeriod: 'Repeat every',
    repeatUnit: 'Repeat unit',
    units: {
      day: 'Days',
      week: 'Weeks',
      month: 'Months',
    },
    repeatHint: 'The visit number goes up by one with each repeat. With "Days", the length of the visit window is added to the interval.',
    submit: 'Save',
    submitting: 'Processing...',
    errors: {
      dates: 'Please fill in all date fields!',
      window: 'The first visit date must be between the start and end dates!',
      repeatPeriod: 'The repeat interval must be greater than 0!',
    },
    toast: {
      added: 'Visit(s) added!',
      addError: 'The visit could not be added!',
    },
  },
  patientStatus: {
    planned: 'Planned',
    screened: 'Screened',
    screenFail: 'Screen fail',
    enrolled: 'Enrolled',
    active: 'Active',
    onMedication: 'On medication',
    completed: 'Completed',
    withdrawn: 'Withdrawn',
  },
  patientStatusHint: {
    planned: 'Planned to be enrolled in the study',
    screened: 'Screening has been done',
    screenFail: 'Failed screening',
    enrolled: 'Enrolled in the study',
    active: 'Actively taking part in the study',
    onMedication: 'Receiving the study medication',
    completed: 'Completed the study',
    withdrawn: 'Withdrawn from the study',
  },
  patients: {
    title: 'Patients',
    summary: 'Patient summary',
    export: 'Download Excel',
    add: 'Add Patient',
    addFirst: 'Add the first patient',
    stats: {
      total: 'Total patients',
      active: 'Active patients',
      homeVisit: 'Home visit',
    },
    visitDistribution: 'Active patients by visit:',
    visitN: 'Visit {{n}}',
    patientCount_one: '{{count}} patient',
    patientCount_other: '{{count}} patients',
    searchPlaceholder: 'Search patient ID...',
    statusFilter: 'Status filter',
    allStatuses: 'All statuses',
    loading: 'Loading patients...',
    noMatch: 'No patients match the filters.',
    empty: 'This project has no patients yet.',
    showing: 'Showing {{shown}} of {{total}} patients',
    patientId: 'Patient ID',
    status: 'Status',
    enrollmentDate: 'Enrollment date',
    enrollmentShort: 'Enrolled',
    firstDoseDate: 'First dose date',
    currentVisit: 'Current visit',
    homeVisit: 'Home visit',
    homeVisitDates: 'Home visit dates',
    homeVisitInfo: 'Home visit notes',
    notes: 'Notes',
    actions: 'Actions',
    view: 'View details',
    edit: 'Edit',
    delete: 'Delete',
    detailTitle: 'Patient details',
    deleteTitle: 'Delete patient',
    deleteMessage: 'Delete patient {{id}}? This cannot be undone.',
    deleteConfirm: 'Yes, delete',
    toast: {
      loadError: 'Patients could not be loaded!',
      deleted: 'Patient deleted',
      deleteError: 'The patient could not be deleted',
      excelSuccess: 'Excel file downloaded',
      excelError: 'The Excel file could not be created',
    },
  },
  addPatient: {
    addTitle: 'Add Patient',
    editTitle: 'Edit Patient',
    save: 'Save',
    update: 'Update',
    basics: 'Basics',
    patientId: 'Patient ID',
    patientIdPlaceholder: 'e.g. PAT-001',
    status: 'Status',
    dates: 'Dates',
    enrollmentDate: 'Enrollment date',
    firstDoseDate: 'First dose date (POF)',
    recommended: 'recommended',
    currentVisitNumber: 'Current visit number',
    currentVisitPlaceholder: 'e.g. 3',
    homeVisit: 'Home visit',
    homeVisitDone: 'Home visit done',
    homeVisitDate: 'Home visit date',
    addDate: 'Add date',
    addedDates: 'Added dates:',
    removeDate: 'Remove {{date}}',
    homeVisitInfo: 'Home visit notes',
    homeVisitInfoPlaceholder: 'More about the home visit...',
    notes: 'Notes',
    notesPlaceholder: 'Notes and important information about the patient...',
    errors: {
      patientId: 'Patient ID is required',
      status: 'Status is required',
      enrollmentDate: 'An enrollment date is required for this status',
      firstDoseDate: 'A first dose date is recommended for a patient on medication',
      inUse: 'This patient ID is already in use',
    },
    existing: {
      title: 'This patient is already registered',
      body: '{{id}} is already registered in this project. Use the existing patient instead of creating a new record?',
      note: "The existing patient's details stay as they are; the patient is only added to your list.",
      cancel: 'Cancel',
      use: 'Use Existing Patient',
      linking: 'Adding...',
    },
    toast: {
      chooseDate: 'Please choose a date',
      dateExists: 'This date is already added',
      linked: 'The existing patient was added to your list',
      linkError: 'The patient could not be added to your list',
      fixErrors: 'Please fill in the required fields',
      duplicate: 'This patient ID already exists in this project!',
      updated: 'Patient updated',
      added: 'Patient added',
      updateError: 'The patient could not be updated',
      addError: 'The patient could not be added',
    },
  },
  roles: {
    'backup-personeli': 'Backup staff',
    cra: 'CRA',
    'calisma-baslatma': 'Study start-up lead',
    'egitim-sorumlusu': 'Training lead',
    'finans-uzmani': 'Finance specialist',
    'ik-uzmani': 'HR specialist',
    'it-uzmani': 'IT specialist',
    'is-gelistirme': 'Business development specialist',
    'kalite-muduru': 'Quality manager',
    'klinik-hemsire': 'Clinical research nurse',
    'proje-muduru': 'Project manager',
    'saha-koordinatoru': 'Site coordinator',
    'saha-koordinatoru-sorumlusu': 'Lead site coordinator',
    'smo-lead': 'SMO lead',
  },
  workers: {
    title: 'Project Team',
    export: 'Download Table',
    add: 'Add to Project',
    addFirst: 'Add the first team member',
    summary: 'Team summary',
    stats: {
      total: 'Team members',
      active: 'Active',
      passive: 'Inactive',
      left: 'Left',
    },
    status: {
      active: 'Active',
      passive: 'Inactive',
      left: 'Left',
    },
    searchPlaceholder: 'Search team...',
    loading: 'Loading team...',
    empty: 'No one is working on this project yet.',
    noMatch: 'No team members match your search',
    showing: 'Showing {{shown}} of {{total}} team members',
    selectHint: 'Select someone from the list to see details and actions.',
    columns: {
      name: 'First name',
      surname: 'Last name',
      roles: 'Roles',
      centers: 'Sites',
      assignedAt: 'Assigned on',
      status: 'Status',
    },
    centerCount_one: '{{count}} site',
    centerCount_other: '{{count}} sites',
    leftCount_one: '{{count}} left',
    leftCount_other: '{{count}} left',
    leftHint: 'Records at the sites they left are kept',
    detail: {
      title: 'Team member details',
      activeCenters: 'Sites',
      roleCount: 'Roles',
      totalAssignments: 'Assignments',
      assignedAt: 'Assigned on',
      roles: 'Roles',
      assignments: 'Sites and roles',
      info: 'Notes',
      unknownCenter: 'Unknown site',
      leftOn: 'Left ({{date}})',
    },
    actions: {
      edit: 'Edit',
      upload: 'Upload Files',
      remove: 'Remove from Project',
      removeHint: "Permanently deletes all of this person's assignments in the project",
    },
    remove: {
      title: 'Remove from Project',
      lead: "All of {{name}}'s assignments in {{project}} will be permanently deleted:",
      warning: 'This cannot be undone: the rate and file records attached to these assignments are lost with them. To hide the person but keep their history, set them inactive or mark them as having left a site from the Edit screen instead.',
      cancel: 'Cancel',
      confirm: 'Delete Permanently',
      removing: 'Deleting...',
    },
    toast: {
      loadError: 'The team could not be loaded!',
      tariffsError: 'The rate list could not be loaded!',
      centersError: 'The site list could not be loaded!',
      dataError: 'The data could not be loaded!',
      managerExists: 'This site already has someone in charge!',
      required: 'Please fill in the required fields!',
      alreadyAssigned: 'This person already works on this project!',
      added: 'Added to the project!',
      addError: 'The person could not be added!',
      updated: 'Details updated!',
      updateError: 'The details could not be updated!',
      projectMissing: 'The project record was not found!',
      removed: '{{name}} was removed from the project.',
      removeError: 'The person could not be removed from the project!',
    },
  },
  workerForm: {
    addTitle: 'Add to Project',
    editTitle: 'Edit Team Member',
    employee: 'Employee',
    employeePlaceholder: 'Choose or search an employee',
    roles: 'Roles',
    rolesPlaceholder: 'Choose roles',
    roleSearch: 'Search roles...',
    rolesSelected_one: '{{count}} role selected',
    rolesSelected_other: '{{count}} roles selected',
    centers: 'Sites',
    centersPlaceholder: 'Choose sites',
    centerSearch: 'Search sites...',
    centersSelected_one: '{{count}} site selected',
    centersSelected_other: '{{count}} sites selected',
    deletedCenter: 'Deleted',
    selectedCenters: 'Selected sites',
    removeCenter: 'Remove {{name}}',
    archiveCenter: 'Mark as having left {{name}}',
    archiveCenterHint: 'Mark as having left this site',
    fteNote: 'Use the Employee Analysis page to manage FTE',
    archivedCenters: 'Sites they left',
    unknownCenter: 'Unknown site',
    leftOn: 'Left · {{date}}',
    willLeave: 'Will leave',
    restoreCenter: 'Add {{name}} back',
    archivedNote: 'Past records, uploaded files and FTE details at these sites are kept.',
    workerType: 'Type',
    primer: 'Primary',
    backup: 'Backup',
    blindStatus: 'Blinding',
    blinded: 'Blinded',
    unblinded: 'Unblinded',
    status: 'Status',
    info: 'Notes',
    infoPlaceholder: 'Add a note...',
    managerWarning: 'Someone is already assigned to this site and role!',
    add: 'Add',
    adding: 'Adding...',
    update: 'Update',
    updating: 'Updating...',
  },
  workerFiles: {
    title: 'Presentation Files',
    type: 'Presentation',
    center: 'Site',
    centerHint: 'The file is added to the assignment at this site',
    drop: 'Drag your presentation file here',
    list_one: 'Uploaded presentation files ({{count}})',
    list_other: 'Uploaded presentation files ({{count}})',
  },
  card: {
    title: 'Project Card',
    edit: 'Edit',
    summary: 'Summary',
    activeWorkers: 'Active staff',
    centers: 'Centres',
    patients: 'Patients',
    visits: 'Visits',
    info: 'Project details',
    code: 'Project code',
    name: 'Project name',
    approval: 'Approvals and ethics committee',
    finance: 'Finance',
    fixedRate: 'Fixed rate',
    centersTitle: 'Centres ({{count}})',
    noCenters: 'No centres are linked to this project.',
    loadError: 'Project card details could not be loaded',
  },
};

export default projectManagement;
